<script lang="ts" setup>
	import { computed } from 'vue'

	import { Company } from '@/modules/companies/interfaces/company'

	import HotelRatePaymentOptions from './HotelRatePaymentOptions.vue'
	import PaymentMethodOption from './ui/PaymentMethodOption.vue'
	import SectionHeader from './ui/SectionHeader.vue'

	interface Card {
		id: string
		name: string
		alias?: string
		institution: string
		holder_name: string
		ownership?: 'user' | 'company'
	}

	interface Credit {
		availablePercentage?: number
	}

	interface Props {
		paymentOptions: {
			name: string
			value: string
			icon: string
		}[]
		attemptedToSubmit?: boolean
		paymentMethodDirty?: boolean
		validationMessage?: string
		companyCards?: Card[]
		userCards?: Card[]
		travelerCards?: Card[]
		credit?: Credit | null
		company: Company
		isInternational?: boolean
		hotelCountryCode?: string
		paymentMethod?: string
		paymentModel?: string
		memberId?: number
		travelerEmail?: string
		travelerId?: number
		travelerUserId?: number
		travelerHasBillingInfo?: boolean
	}

	const props = withDefaults(defineProps<Props>(), {
		attemptedToSubmit: false,
		paymentMethodDirty: false,
		validationMessage: '',
		companyCards: () => [],
		userCards: () => [],
		travelerCards: () => [],
		credit: null,
	})

	const selectedPaymentMethod = defineModel<string>('selectedPaymentMethod')
	const selectedCardId = defineModel<string>('selectedCardId')

	const isInternalPaymentMethodsOption = computed(() => {
		// Solo el flujo pago-en-destino (Amadeus internacional) restringe a tarjeta de
		// garantía. Hotelbeds es PREPAGO (paymentModel='prepaid'): se cobra por adelantado
		// con crédito/OpenPay → NO filtrar esos métodos (si no, el cliente se queda sin
		// forma de pago válida).
		if (
			props.isInternational &&
			props.hotelCountryCode &&
			props.paymentModel !== 'prepaid'
		) {
			return props.paymentOptions.filter((opt) => {
				return (
					opt.value !== 'credit' &&
					opt.value !== 'userCards' &&
					opt.value !== 'companyCards'
				)
			})
		}
		return props.paymentOptions
	})
</script>

<template>
	<!-- Payment Method -->
	<div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
		<SectionHeader
			title="Método de pago"
			subtitle="Seleccione su forma de pago preferida"
			required
			icon="payment"
			icon-bg-color="blue"
			icon-color="blue"
		/>

		<div class="p-4 lg:p-6">
			<div
				v-if="isInternational && hotelCountryCode !== 'MX' && paymentModel !== 'prepaid'"
				class="flex items-start gap-3 p-4 mb-4 bg-amber-50 border border-amber-200 rounded-lg"
			>
				<svg
					class="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
					fill="currentColor"
					viewBox="0 0 20 20"
				>
					<path
						fill-rule="evenodd"
						d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
						clip-rule="evenodd"
					/>
				</svg>
				<div>
					<p class="text-sm font-medium text-amber-800 font-inter">
						Información importante
					</p>
					<p class="text-sm text-amber-700 font-inter mt-1">
						El pago de esta reservación se realizará directamente en el hotel al
						momento de su llegada (check-in). Por favor, asegúrese de contar con
						una forma de pago válida al registrarse.
					</p>
				</div>
			</div>

			<div class="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4 mb-5">
				<PaymentMethodOption
					v-for="(option, i) in isInternalPaymentMethodsOption"
					:key="i"
					:name="option.name"
					:icon="option.icon"
					:selected="selectedPaymentMethod === option.value"
					@select="selectedPaymentMethod = option.value"
				/>
			</div>

			<div v-if="selectedPaymentMethod">
				<HotelRatePaymentOptions
					:payment-options="isInternalPaymentMethodsOption"
					v-model:selected-payment-method="selectedPaymentMethod"
					:company-cards="companyCards"
					:user-cards="userCards"
					:traveler-cards="travelerCards"
					:credit="credit"
					v-model:selected-card-id="selectedCardId"
					:company="company"
					:is-international="isInternational"
					:hotel-country-code="hotelCountryCode"
					:member-id="memberId"
					:traveler-email="travelerEmail"
					:traveler-id="travelerId"
					:traveler-user-id="travelerUserId"
				/>

				<!-- Validation message -->
				<span
					v-show="(attemptedToSubmit || paymentMethodDirty) && !selectedPaymentMethod"
					class="text-xs lg:text-sm text-red-500 font-inter block mt-4"
				>
					{{ validationMessage }}
				</span>
			</div>
		</div>
	</div>
</template>
